import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/DashboardLayout";
import { adminNav } from "@/config/nav";
import api, { formatApiError } from "@/lib/api";
import { MagnifyingGlass, Plus, Trash, FloppyDisk } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const PRESET_PATHS = [
  "/hyderabad-farmhouses", "/hyderabad-resorts", "/hyderabad-banquet-halls", "/hyderabad-function-halls",
  "/farmhouses/moinabad", "/farmhouses/shamirpet", "/farmhouses/chevella", "/farmhouses/gandipet",
  "/resorts/shamirpet", "/banquet-halls/hyderabad", "/function-halls/hyderabad",
];
const blank = { path: "", title: "", meta_description: "", h1: "", intro: "", faqs: [], image: "", canonical: "" };

export default function AdminSeo() {
  const [pages, setPages] = useState([]);
  const [f, setF] = useState(blank);

  const load = () => api.get("/admin/seo-pages").then((r) => setPages(r.data));
  useEffect(() => { load(); }, []);

  const edit = (p) => setF({ ...blank, ...p, faqs: p.faqs || [] });
  const pick = (path) => { const existing = pages.find((p) => p.path === path); setF(existing ? { ...blank, ...existing, faqs: existing.faqs || [] } : { ...blank, path }); };
  const setK = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const setFaq = (i, key, val) => { const faqs = [...f.faqs]; faqs[i] = { ...faqs[i], [key]: val }; setF({ ...f, faqs }); };
  const addFaq = () => setF({ ...f, faqs: [...f.faqs, { q: "", a: "" }] });
  const rmFaq = (i) => setF({ ...f, faqs: f.faqs.filter((_, idx) => idx !== i) });

  const save = async () => {
    if (!f.path) return toast.error("Choose or enter a page path.");
    try { await api.put("/admin/seo-pages", { ...f, faqs: f.faqs.filter((x) => x.q && x.a) }); toast.success("SEO content saved"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (p) => { await api.delete(`/admin/seo-pages?path=${encodeURIComponent(p.path)}`); if (f.path === p.path) setF(blank); load(); };

  return (
    <DashboardLayout nav={adminNav} title="Admin">
      <h1 className="font-heading text-2xl font-semibold text-stone-900 md:text-3xl">SEO Content Manager</h1>
      <p className="mt-1 text-sm text-stone-500">Customise the title, intro and FAQs of each landing page. Write genuinely helpful copy — no keyword stuffing.</p>

      <div className="mt-5 grid gap-6 lg:grid-cols-[280px_1fr]">
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-stone-400">Landing pages</p>
          <div className="flex flex-wrap gap-2 lg:flex-col lg:items-stretch">
            {PRESET_PATHS.map((path) => {
              const done = pages.some((p) => p.path === path);
              return <button key={path} data-testid={`seo-pick-${path}`} onClick={() => pick(path)} className={`rounded-xl border px-3 py-2 text-left text-sm ${f.path === path ? "border-primary bg-primary/5 text-primary" : "border-stone-200 text-stone-600 hover:bg-stone-50"}`}>{path}{done ? " ✓" : ""}</button>;
            })}
          </div>
        </div>

        <div className="rounded-2xl border border-stone-200 bg-white p-6">
          <div><Label className="text-xs text-stone-500">Page path</Label><Input data-testid="seo-path" value={f.path} onChange={setK("path")} className="mt-1 rounded-xl font-mono" placeholder="/farmhouses/moinabad" /></div>
          <div className="mt-3"><Label className="text-xs text-stone-500">SEO title</Label><Input data-testid="seo-title" value={f.title} onChange={setK("title")} className="mt-1 rounded-xl" placeholder="Farmhouses in Moinabad, Hyderabad" /></div>
          <div className="mt-3"><Label className="text-xs text-stone-500">Meta description</Label><Textarea value={f.meta_description} onChange={setK("meta_description")} className="mt-1 rounded-xl" /></div>
          <div className="mt-3"><Label className="text-xs text-stone-500">H1 heading</Label><Input value={f.h1} onChange={setK("h1")} className="mt-1 rounded-xl" /></div>
          <div className="mt-3"><Label className="text-xs text-stone-500">Intro paragraph</Label><Textarea data-testid="seo-intro" value={f.intro} onChange={setK("intro")} className="mt-1 min-h-[90px] rounded-xl" /></div>

          <div className="mt-4 flex items-center justify-between">
            <Label className="text-xs font-semibold uppercase tracking-wide text-stone-400">FAQs</Label>
            <button onClick={addFaq} className="flex items-center gap-1 text-xs text-primary"><Plus size={14} /> Add FAQ</button>
          </div>
          <div className="mt-2 space-y-3">
            {f.faqs.map((faq, i) => (
              <div key={i} className="rounded-xl border border-stone-100 p-3">
                <div className="flex items-center gap-2">
                  <Input value={faq.q} onChange={(e) => setFaq(i, "q", e.target.value)} className="rounded-lg" placeholder="Question" />
                  <button onClick={() => rmFaq(i)} className="text-stone-400 hover:text-destructive"><Trash size={16} /></button>
                </div>
                <Textarea value={faq.a} onChange={(e) => setFaq(i, "a", e.target.value)} className="mt-2 rounded-lg" placeholder="Answer" />
              </div>
            ))}
          </div>

          <Button data-testid="seo-save" onClick={save} className="mt-4 gap-1 rounded-xl bg-primary hover:bg-green-900"><FloppyDisk size={16} /> Save content</Button>
          {f.path && pages.some((p) => p.path === f.path) && <Button variant="outline" onClick={() => del(f)} className="ml-2 mt-4 rounded-xl text-destructive">Delete</Button>}
        </div>
      </div>
    </DashboardLayout>
  );
}
